import React, { useState } from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useTheme,
} from "@chakra-ui/core"
import { FlagIcon } from "@heroicons/react/24/outline"

import { gql, useClient, useMutation } from "hooks/urql"

import {
  FormFailures,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"
import ReactModalFooter from "components/ModalWithProvidedBody/Footer"

export type ReportMessageButtonProps = {
  messageId: string
  userType: "Teacher" | "Student" | "Tutor" | "Parent"
}

const ReportMessageButton: React.FC<ReportMessageButtonProps> = ({
  messageId,
  userType,
}) => {
  const [open, setOpen] = useState(false)
  const theme: any = useTheme()
  const client = useClient()
  const [, runMutation]: any[] = useMutation(reportMessageMutation, client)

  const reportMessage = (values, actions) => {
    runMutation(values)
      .then(
        result => {
          const { failures } = result.data.reportMessage
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            setOpen(false)
            NotificationManager.success("Message reported.")
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <>
      <FlagIcon
        className="h-6 w-6 cursor-pointer text-red-500"
        onClick={() => setOpen(true)}
      />

      {userType === "Teacher" ? (
        <Modal isOpen={open} onClose={() => setOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <Formik initialValues={{ messageId }} onSubmit={reportMessage}>
              {({ isSubmitting }) => (
                <Form>
                  <ModalHeader>Report Message</ModalHeader>
                  <ModalBody>
                    If you feel the content of this message is inappropriate or
                    unprofessional, please click "Report" and an admin will be
                    prompted to review it.
                  </ModalBody>

                  <ModalFooter>
                    <div className="flex w-full flex-col space-y-4">
                      <FormFailures />
                      <div className="flex w-full justify-between">
                        <Button
                          {...theme.buttonVariants.view}
                          onClick={() => setOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          {...theme.buttonVariants.cloudroom}
                          disabled={isSubmitting}
                        >
                          Report
                        </Button>
                      </div>
                    </div>
                  </ModalFooter>
                </Form>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      ) : (
        <ModalWithProvidedBody
          modalTitle="Report Message"
          modalClassName="bootstrap-modal"
          hideTrigger
          closeModal={() => setOpen(false)}
          isOpen={open}
        >
          {({ closeModal }) => (
            <Formik initialValues={{ messageId }} onSubmit={reportMessage}>
              <Form>
                <div className="modal-body">
                  If you feel the content of this message is inappropriate or
                  unprofessional, please click "Report" and an admin will be
                  prompted to review it.
                </div>

                <ReactModalFooter
                  cancelAction={closeModal}
                  isDangerous
                  submitText="Report"
                />
              </Form>
            </Formik>
          )}
        </ModalWithProvidedBody>
      )}
    </>
  )
}

const reportMessageMutation = gql`
  mutation ReportMessage($messageId: ID!) {
    reportMessage(messageId: $messageId) {
      failures {
        message
      }
    }
  }
`

export default ReportMessageButton
